import { Trans, useTranslation } from 'react-i18next';

import { useContactOptions } from './useContactOptions';
import { useContent } from './useContent';
import { useFeeTranslations } from './useFeeTranslations';
import { useConditionsQuery, useParamsQuery } from './useQueries';

import type { CoverageKey } from '../models/coverage';
import type { CoverageContent } from '@insify/funnel-components';
import type { CountryCode } from '@insify/global-countries';

export const useCoverageContent = (): CoverageContent<CoverageKey> => {
  const { contactOptions, contactOptionsDescription } = useContactOptions();
  const { t, i18n } = useTranslation(['funnel', 'common']);
  const { coverageHeader, coverageReceipt } = useContent() || {};
  const { data: { publicLiability } = {} } = useParamsQuery();
  const { dailyCancellable = true } = useConditionsQuery()?.data?.professionalLiability ?? {};
  const hasPublicLiabilitySelected = Number(publicLiability?.coverLimit.cents) > 1;

  return {
    country: i18n.language as Lowercase<CountryCode>,
    currency: 'EUR',
    introContent: coverageHeader,
    // coverageReceipt (from Contentful) has "Sans engagement" in it, which is not relevant when dailyCancellable is false
    receiptContent: dailyCancellable ? coverageReceipt : undefined,
    receiptDiscountContent: <></>,
    receiptBelowSubmit: <></>,
    translations: {
      back: t('common:back'),
      headerConditions: t('funnel:coverage_header_conditions'),
      perMonth: t('funnel:coverage_per_month'),
      perYear: t('funnel:coverage_per_year'),
      receiptDeductible: t('funnel:coverage_receipt_deductible'),
      receiptTotal: t('funnel:coverage_receipt_total'),
      receiptSubmit: t('funnel:coverage_receipt_submit'),
      // This receipt item is included in the professionalLiability label
      receiptPublicLiability: '',
      receiptProfessionalLiability: hasPublicLiabilitySelected
        ? t('funnel:coverage_receipt_liability_with_public_liability')
        : t('funnel:coverage_receipt_liability'),
      receiptLegalProtection: t('funnel:add_on_legal_protection_title'),
      receiptPropertyMultiRisk: t('funnel:coverage_receipt_property_multi_risk'),
      totalPremiumContinue: t('funnel:coverage_total_premium_continue'),
      receiptUSP: dailyCancellable ? <Trans i18nKey='funnel:coverage_receipt_usp_no_commitment' /> : undefined,
      fees: useFeeTranslations(),
    },
    contactOptions: contactOptions,
    contactOptionsTitle: t('funnel:coverage_contact_title'),
    contactOptionsDescription: contactOptionsDescription,
  };
};
